import { countries } from "@redotech/locale/countries";
import { isCountryCode } from "@redotech/locale/country-codes";
import { useHandler } from "@redotech/react-util/hook";
import {
  DeliveryLocationRule,
  DeliveryLocationRulesSegment,
  DeliveryLocationRulesSegmentSchema,
  SegmentListSingleItemMembershipOperator,
} from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import { RcoSegmentStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import EditPencilIcon from "@redotech/redo-icons/edit-01.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { PartialWithRequiredType } from "../..";
import { LocationRuleSelectionModal } from "./location-rule-selection-modal";

function ruleLabel(rule: DeliveryLocationRule): string {
  const countryName = isCountryCode(rule.countryCode)
    ? (countries.find((c) => c.code === rule.countryCode)?.name ??
      rule.countryCode)
    : rule.countryCode;
  const provinceCount = rule.provinces?.length ?? 0;
  return provinceCount > 0
    ? `${countryName} (${provinceCount} ${provinceCount === 1 ? "province" : "provinces"})`
    : countryName;
}

function LocationRuleBadges({ rules }: { rules: DeliveryLocationRule[] }) {
  const { t } = useTranslation();
  if (rules.length === 0) {
    return (
      <Text fontSize="xs" textColor="secondary">
        {t("No locations selected")}
      </Text>
    );
  }
  return (
    <Flex dir="row" gap="sm" wrap="wrap">
      {rules.map((rule) => (
        <RedoBadge
          color="gray"
          key={rule.countryCode}
          size="sm"
          text={ruleLabel(rule)}
        />
      ))}
    </Flex>
  );
}

export const DeliveryLocationRulesInspectorContent = memo(
  function DeliveryLocationRulesInspectorContent({
    step,
  }: {
    step: PartialWithRequiredType<RcoSegmentStep>;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => DeliveryLocationRulesSegmentSchema.parse(step.segment),
      [step.segment],
    );
    const isNotIn =
      segment.operator === SegmentListSingleItemMembershipOperator.NOT_IN;
    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {isNotIn
            ? t("Delivery location is not in")
            : t("Delivery location is in")}
        </Text>
        <LocationRuleBadges rules={segment.locationRules} />
      </Flex>
    );
  },
);

export const EditableDeliveryLocationRulesInspectorContent = memo(
  function EditableDeliveryLocationRulesInspectorContent({
    step,
    setStep,
  }: {
    step: PartialWithRequiredType<RcoSegmentStep>;
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => DeliveryLocationRulesSegmentSchema.parse(step.segment),
      [step.segment],
    );
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleRulesSelected = useHandler((rules: DeliveryLocationRule[]) => {
      const updated: DeliveryLocationRulesSegment = {
        ...segment,
        locationRules: rules,
      };
      setStep({ ...step, segment: updated });
      setIsModalOpen(false);
    });

    return (
      <Flex align="flex-start" dir="column" gap="sm" w="full">
        <LocationRuleBadges rules={segment.locationRules} />
        <Flex justify="flex-end" w="full">
          <RedoButton
            hierarchy="secondary"
            IconLeading={EditPencilIcon}
            onClick={() => setIsModalOpen(true)}
            size="xs"
            text={t("Edit locations")}
          />
        </Flex>
        {isModalOpen && (
          <LocationRuleSelectionModal
            onClose={() => setIsModalOpen(false)}
            onRulesSelected={handleRulesSelected}
            otherConditionRules={[]}
            selectedRules={segment.locationRules}
          />
        )}
      </Flex>
    );
  },
);
